// Touch guard for <input type=range> (founder, 2026-10-08: "too easy to accidentally move sliders
// while scrolling in the menu"). Chromium's native range already lets a vertical swipe scroll its
// sheet (touch-action: pan-y on it changes nothing), but it jumps the value to the finger on
// touchstart, so every scroll or tap that starts on a slider changes it. Under this guard a touch
// moves a slider ONLY on a deliberate sideways drag:
//  - for the life of a touch the guard owns the value: the native handler's trusted input/change
//    events are swallowed and the value put back, so nothing on touch-down or while the scroll is
//    being decided ever changes it (not even for a frame);
//  - the touch becomes a drag once it has moved LOCK_PX sideways and more sideways than vertical,
//    while the browser has not started scrolling; from there the thumb moves by the finger's
//    sideways travel since the lock (relative, so it never jumps), and the guard fires the input
//    events, then one change on release;
//  - a tap does nothing (its touchend is cancelled, so no click / compat mousedown jumps it).
// Mouse, pen-as-mouse and keyboard never reach this code: the input stays a real range.

const LOCK_PX = 10;
const THUMB_PX = 24; // the styled thumb (.sb-range / .sb-stay-range); the travel is the width minus it

type Touch = {
  input: HTMLInputElement;
  id: number;
  x0: number;
  y0: number;
  state: 'pending' | 'drag' | 'scroll';
  value: string; // the value the guard owns while the finger is down
  start: string; // the value at touch-down (a change event fires on release only if it moved)
  lockX: number;
  lockValue: number;
};

let live: Touch | null = null;

function swallowNative(e: Event): void {
  if (!live || e.target !== live.input || !e.isTrusted) return;
  e.stopImmediatePropagation();
  live.input.value = live.value;
}

function find(list: TouchList, id: number): { clientX: number; clientY: number } | null {
  for (let i = 0; i < list.length; i++) if (list[i].identifier === id) return list[i];
  return null;
}

function end(input: HTMLInputElement, e: TouchEvent): void {
  const t = live;
  if (!t || t.input !== input || !find(e.changedTouches, t.id)) return;
  if (t.state !== 'scroll' && e.cancelable) e.preventDefault(); // no tap: no click, no compat mousedown
  if (t.state === 'drag' && input.value !== t.start) input.dispatchEvent(new Event('change', { bubbles: true }));
  // Keep owning the value until the native touchend handling (which runs after the listeners) is done.
  setTimeout(() => { if (live === t) live = null; });
}

let installed = false;

/** Make a range move under touch only on a deliberate sideways drag (see the header). */
export function guardRange(input: HTMLInputElement): void {
  if (!installed) {
    installed = true;
    window.addEventListener('input', swallowNative, true);
    window.addEventListener('change', swallowNative, true);
  }
  input.addEventListener('touchstart', (e) => {
    const p = e.changedTouches[0];
    if (live?.input === input) { live.state = 'scroll'; return; } // a second finger: never a drag
    if (input.disabled || !p) return;
    const state = e.touches.length > 1 ? 'scroll' : 'pending';
    live = { input, id: p.identifier, x0: p.clientX, y0: p.clientY, state, value: input.value, start: input.value, lockX: 0, lockValue: 0 };
  }, { passive: true });
  input.addEventListener('touchmove', (e) => {
    const t = live;
    const p = t && t.input === input ? find(e.touches, t.id) : null;
    if (!t || !p || t.state === 'scroll') return;
    if (t.state === 'pending') {
      const dx = Math.abs(p.clientX - t.x0);
      const dy = Math.abs(p.clientY - t.y0);
      if (!e.cancelable || (dy >= LOCK_PX && dy >= dx)) { t.state = 'scroll'; return; } // !cancelable: the browser is scrolling
      if (dx < LOCK_PX || dx <= dy) return;
      t.state = 'drag';
      t.lockX = p.clientX;
      t.lockValue = Number(input.value);
    }
    e.preventDefault(); // the drag owns this touch: no scroll, and the native handler stays out
    const min = Number(input.min || 0);
    const max = Number(input.max || 100);
    const travel = Math.max(1, input.getBoundingClientRect().width - THUMB_PX);
    const dir = getComputedStyle(input).direction === 'rtl' ? -1 : 1;
    input.value = String(t.lockValue + (dir * (p.clientX - t.lockX) * (max - min)) / travel); // snaps to step
    if (input.value === t.value) return;
    t.value = input.value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }, { passive: false });
  input.addEventListener('touchend', (e) => end(input, e), { passive: false });
  input.addEventListener('touchcancel', (e) => end(input, e));
}
